import { Add as AddIcon, Circle as CircleIcon, Work as WorkIcon } from '@mui/icons-material';
import { Chip, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { SearchObjective, SearchToAdd, SingleSearch } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiAlertTextField from 'features/ui/base-components/pai-alert-text-field';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import CrawlerOption from './crawler-options';

interface Props {
  productGroup: ProductGroups;
  searchToAdd: SearchToAdd;
  isSaveDisabled?: boolean;
  addSearchesLoading?: boolean;
  selectedContactSearchObjective: SearchObjective;

  handleAddOwnLinkedinSearch?: () => void;

  handleAddOwnLinkedinSearchWithSkipQualification?: () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleJobTitlesChange?: (titles: string[]) => void;
}

const EnterSearchDetails: React.FC<Props> = ({
  productGroup,
  searchToAdd,
  isSaveDisabled = true,
  addSearchesLoading = false,
  selectedContactSearchObjective,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleJobTitlesChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [currentJobTitle, setCurrentJobTitle] = useState<string>('');

  const isPostings = searchToAdd.key === 'postings';
  const isJobPostings = searchToAdd.key === 'jobPostings';
  const crawlerOptions = searchToAdd.crawlerOptions || {};
  const isValidUrl = searchToAdd.search.url === '' || new RegExp(searchToAdd.regex).test(searchToAdd.search.url);
  const jobTitles = searchToAdd.search.targetJobTitles || [];

  const handleAddJobTitle = () => {
    const trimmedTitle = currentJobTitle.trim();
    if (trimmedTitle !== '' && !jobTitles.includes(trimmedTitle)) {
      const newTitles = [...jobTitles, trimmedTitle];
      setCurrentJobTitle('');
      handleJobTitlesChange?.(newTitles);
    }
  };

  const handleRemoveJobTitle = (titleToRemove: string) => {
    const newTitles = jobTitles.filter((title) => title !== titleToRemove);
    handleJobTitlesChange?.(newTitles);
  };

  const handleJobTitleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentJobTitle(event.target.value);
  };

  const handleJobTitleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddJobTitle();
    }
  };

  return (
    <div className={classes.container}>
      <Stack gap={1.5}>
        <Stack key={searchToAdd.key} className={classes.searchContainer}>
          {/** Header */}
          <Stack className={classes.searchContainerHeader} direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} gap={1}>
            <Typography sx={{ lineHeight: 1.2 }}>
              <span className={classes.searchToAddTitle}>{searchToAdd.title}:</span>
              <span className={classes.searchToAddSubtitle}> {searchToAdd.subTitle}</span>
            </Typography>
          </Stack>

          <Stack className={classes.searchContainerBody} gap={2}>
            {/** Name your search */}
            <Stack gap={1}>
              <Typography className={classes.label}>{t('nameYourSearch')}</Typography>
              <PaiAlertTextField
                value={searchToAdd.search.name}
                placeholder={t('giveLinkedinSearchName')}
                fullWidth
                onChange={handleOwnSearchFieldChange('name')}
              />
            </Stack>

            {/** Insert Linkedin URL */}
            <Stack gap={1}>
              <Typography className={classes.label}>{isJobPostings ? t('jobSearchUrlRequired') : t('insertLinkedinUrl')}</Typography>
              <div style={{ paddingRight: 'calc(20px + 16px)' }}>
                <PaiAlertTextField
                  value={searchToAdd.search.url}
                  placeholder={isJobPostings ? t('pasteLinkedinJobsPageUrl') : t('pasteLinkedinUrl')}
                  fullWidth
                  onChange={handleOwnSearchFieldChange('url')}
                  error={!isValidUrl}
                />
              </div>
            </Stack>

            {/** Target Job Titles - only for job search */}
            {isJobPostings && (
              <Stack gap={1}>
                <Typography className={classes.label}>{t('targetJobTitlesRequired')}</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  <PaiTextField
                    value={currentJobTitle}
                    placeholder={t('enterJobTitlePlaceholder')}
                    onChange={handleJobTitleInputChange}
                    onKeyDown={handleJobTitleKeyDown}
                    disabled={addSearchesLoading}
                    variant={'white'}
                  />
                  <PaiIconButton size="small" onClick={handleAddJobTitle} disabled={!currentJobTitle.trim() || addSearchesLoading}>
                    <AddIcon />
                  </PaiIconButton>
                </Stack>

                {/* Job Titles Display Section */}
                <Stack gap={2} sx={{ marginTop: 2 }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <CircleIcon className={classes.redIndicator} />
                    <Typography className={classes.sectionLabel}>{t('targetJobTitlesCount', { count: jobTitles.length })}</Typography>
                  </Stack>

                  <Stack className={classes.chipContainer} direction="row" gap={1} flexWrap="wrap">
                    {jobTitles.length > 0 ? (
                      jobTitles.map((title) => (
                        <Chip
                          key={title}
                          className={classes.jobTitleChip}
                          variant={'outlined'}
                          size={'small'}
                          label={
                            <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                              <WorkIcon className={classes.chipIcon} />
                              {title}
                            </Stack>
                          }
                          onDelete={() => handleRemoveJobTitle(title)}
                          disabled={addSearchesLoading}
                        />
                      ))
                    ) : (
                      <Stack className={classes.emptyState} direction="row" alignItems="center" gap={1}>
                        <WorkIcon className={classes.emptyIcon} />
                        <Typography className={classes.emptyText}>{t('noJobTitlesAddedYetAddATitleAboveToGetStarted')}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>

          <Stack className={classes.searchContainerBody} gap={2} sx={(theme) => ({ borderTop: `1px solid ${theme.palette.greys.light}` })}>
            <Stack gap={1}>
              <Typography className={classes.boldText}>{t('setTotalContactsTitle')}</Typography>
              <Typography className={classes.greyText}>{t('setTotalContactsDescriptionShort')}</Typography>
            </Stack>
            <Stack gap={1}>
              <PaiTextField
                value={searchToAdd.search.numberOfContactsToLoad}
                variant={'white'}
                type={'number'}
                onChange={handleOwnSearchFieldChange('numberOfContactsToLoad')}
                style={{ maxWidth: '350px' }}
              />
              {searchToAdd.search.numberOfContactsToLoad > 150 ? (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <PaiHelpIcon tooltipText={false} variant={'warning'} />
                  <Typography className={classes.warningText}>{t('contactsLoadingLimitWarning', { count: 150 })}</Typography>
                </Stack>
              ) : null}
            </Stack>
          </Stack>

          {/**
           * If this is the 'postings' objective => show the integrated CrawlerOption
           */}
          {isPostings && (
            <Stack className={classes.searchContainerBody} sx={(theme) => ({ borderTop: `1px solid ${theme.palette.greys.light}` })}>
              <CrawlerOption
                crawlerOptions={{
                  includeReactions: !!crawlerOptions.includeReactions,
                  includeComments: !!crawlerOptions.includeComments,
                  includeAuthor: !!crawlerOptions.includeAuthor,
                  includedProfileDegrees: crawlerOptions.includedProfileDegrees || [],
                }}
                handlePeopleTypeChange={handlePeopleTypeChange ? (type) => handlePeopleTypeChange(type) : () => () => {}}
                handleProfileDegreeChange={handleProfileDegreeChange ? (value) => handleProfileDegreeChange(value) : () => () => {}}
              />
            </Stack>
          )}
        </Stack>

        <Stack direction={'row'} justifyContent={'flex-end'}>
          {selectedContactSearchObjective.key === 'recruiterProjects' ? (
            <PaiButton
              text={getLocalizedText('addContactSearch', productGroup, t)}
              border="rounded"
              action={handleAddOwnLinkedinSearchWithSkipQualification}
              disabled={isSaveDisabled || !isValidUrl}
              loading={addSearchesLoading}
            />
          ) : (
            <PaiButton
              text={getLocalizedText('addContactSearch', productGroup, t)}
              border="rounded"
              action={handleAddOwnLinkedinSearch}
              disabled={isSaveDisabled || !isValidUrl}
              loading={addSearchesLoading}
            />
          )}
        </Stack>
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: '#FFF',
    borderRadius: '12px',
    width: '100%',
    padding: '12px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  warningText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.warning.main,
  },
  placeholderText: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 2,
    color: theme.palette.greys.medium,
  },
  placeholderContainer: {
    minHeight: '400px',
    background: theme.palette.backgrounds.main_new,
    borderRadius: '9px',
  },
  searchContainer: {
    background: theme.palette.backgrounds.main_new,
    borderRadius: '9px',
  },
  searchContainerHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  searchContainerBody: {
    padding: '16px 24px',
  },
  searchContainerFooter: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
  searchToAddTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  searchToAddSubtitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    '&.error': {
      color: theme.palette.error.main,
    },
  },
  label: {
    fontSize: theme.typography.pxToRem(15),
    textDecoration: 'underline',
  },
  chip: {
    fontSize: theme.typography.pxToRem(14),
    backgroundColor: '#e3f2fd',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    margin: '4px',
    padding: '8px 12px',
    fontWeight: 'bold',
    border: `2px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
    },
    '& .MuiChip-deleteIcon': {
      fontSize: theme.typography.pxToRem(18),
      color: theme.palette.primary.main,
      '&:hover': {
        color: '#fff',
      },
    },
  },
  redIndicator: {
    fontSize: theme.typography.pxToRem(8),
    color: theme.palette.error.main,
    marginRight: theme.spacing(0.5),
  },
  sectionLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  chipContainer: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
    minHeight: theme.spacing(6),
  },
  jobTitleChip: {
    borderColor: theme.palette.greys.light,
    borderRadius: '6px',
    backgroundColor: '#FFF',
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
  },
  chipIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.palette.grey[500],
  },
  emptyIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.grey[400],
  },
  emptyText: {
    fontSize: theme.typography.pxToRem(14),
    fontStyle: 'italic',
    color: theme.palette.grey[500],
  },
}));

export default EnterSearchDetails;
